import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Lock, CheckCircle2, Sparkles, RotateCcw, ArrowRight, ShieldCheck, Compass } from 'lucide-react';

/**
 * HP.5 Gated Overwhelm Display
 * Shows free classification + reframes, then gated triage preview.
 */

const CONFIDENCE_LABEL = {
  high: { label: 'High confidence', tone: 'text-emerald-400' },
  medium: { label: 'Good match', tone: 'text-teal-400' },
  low: { label: 'Possible match', tone: 'text-gray-400' },
};

const GatedOverwhelmDisplay = ({ data, onStartFresh, accentColor = '#14b8a6' }) => {
  const navigate = useNavigate();

  const classification = data?.classification || {};
  const reframes = data?.reframes || [];
  const triage = data?.triage_preview || {};
  const gatedFeatures = data?.gated_features || [];

  const conf = CONFIDENCE_LABEL[classification.confidence] || CONFIDENCE_LABEL.low;

  const handleUnlock = () => {
    // Persist pending unlock so post-signup we can pick it up (same convention as other hero pages)
    try {
      sessionStorage.setItem('rv_pending_unlock', JSON.stringify({
        review_id: data?.review_id,
        input_hash: data?.input_hash,
        flow_type: 'overwhelm',
      }));
    } catch {
      /* ignore */
    }
    navigate(`/login?flow=overwhelm&rv_id=${encodeURIComponent(data?.review_id || '')}`);
  };

  return (
    <div data-testid="overwhelm-result" className="space-y-8">
      {/* Classification card — free */}
      <section
        className="p-6 sm:p-8 rounded-2xl border"
        style={{ backgroundColor: `${accentColor}08`, borderColor: `${accentColor}40` }}
        data-testid="overwhelm-classification"
      >
        <div className="flex items-center gap-2 mb-3">
          <Compass className="w-4 h-4" style={{ color: accentColor }} />
          <span className="text-xs uppercase tracking-wider font-semibold" style={{ color: accentColor }}>Your situation map</span>
        </div>
        <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">
          {classification.primary_type_name || 'General Situation'}
        </h2>
        {classification.description ? (
          <p className="text-gray-300 text-base leading-relaxed mb-3">{classification.description}</p>
        ) : null}
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <span className={conf.tone}>✓ {conf.label}</span>
          {Array.isArray(classification.matched_keywords) && classification.matched_keywords.length > 0 ? (
            <span className="text-gray-500">
              • Signals: {classification.matched_keywords.slice(0, 4).map((k) => `"${k}"`).join(', ')}
            </span>
          ) : null}
        </div>

        {Array.isArray(classification.secondary_types) && classification.secondary_types.length > 0 ? (
          <div className="mt-5 pt-4 border-t border-white/5">
            <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-2">
              Also shows patterns of
            </p>
            <div className="flex flex-wrap gap-2">
              {classification.secondary_types.map((st) => (
                <span
                  key={st.type}
                  className="text-xs rounded-full px-2.5 py-1 bg-gray-800/80 border border-gray-700 text-gray-300"
                >
                  {st.type_name}
                </span>
              ))}
            </div>
          </div>
        ) : null}
      </section>

      {/* Reframes — free */}
      {reframes.length > 0 ? (
        <section data-testid="overwhelm-reframes">
          <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
            <Sparkles className="w-4 h-4" style={{ color: accentColor }} /> Reframes for this situation
          </h3>
          <div className="space-y-3">
            {reframes.map((text, i) => (
              <div
                key={i}
                className="p-4 rounded-xl border border-gray-800/60 bg-gray-900/40"
                data-testid={`overwhelm-reframe-${i}`}
              >
                <p className="text-gray-200 leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      {/* Triage plan — GATED */}
      <section data-testid="overwhelm-triage-gated">
        <h3 className="text-lg font-semibold text-white mb-3">Your triage plan</h3>
        <div className="relative rounded-xl border border-dashed border-gray-700 bg-gray-900/40 p-5 overflow-hidden">
          <div className="space-y-3">
            {(triage.preview_titles || []).map((title, i) => (
              <div
                key={i}
                className="flex items-start gap-3 p-3 rounded-lg bg-gray-950/60 border border-gray-800/40"
                data-testid={`overwhelm-triage-item-${i}`}
              >
                <div className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center font-bold text-xs"
                     style={{ backgroundColor: `${accentColor}20`, color: accentColor }}>
                  {i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-medium text-sm">{title}</p>
                  <div className="mt-2 space-y-1.5">
                    <div className="h-3 bg-gray-700/40 rounded w-11/12" />
                    <div className="h-3 bg-gray-700/30 rounded w-8/12" />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Lock overlay */}
          <div className="absolute inset-0 backdrop-blur-sm bg-gray-950/60 flex items-center justify-center z-10">
            <div className="text-center px-6">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full mb-3"
                   style={{ backgroundColor: `${accentColor}20` }}>
                <Lock className="w-5 h-5" style={{ color: accentColor }} />
              </div>
              <p className="text-white font-semibold mb-1">
                Unlock your {triage.gated_count || 3}-step triage plan
              </p>
              <p className="text-sm text-gray-400 mb-5 max-w-sm mx-auto">
                Free signup to get the full plan, a companion chat session on this exact situation, and it saved to your dashboard.
              </p>
              <Button
                data-testid="overwhelm-unlock-btn"
                onClick={handleUnlock}
                className="text-white font-semibold px-6 py-5"
                style={{ backgroundColor: accentColor }}
              >
                Unlock full triage — free
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* What you'll get */}
      {gatedFeatures.length > 0 ? (
        <section className="p-5 rounded-xl bg-gray-900/40 border border-gray-800/60" data-testid="overwhelm-gated-features">
          <p className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
            <ShieldCheck className="w-4 h-4" style={{ color: accentColor }} /> After signup you also get
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {gatedFeatures.map((f) => (
              <div key={f} className="flex items-start gap-2 text-sm text-gray-300">
                <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: accentColor }} />
                {f.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
              </div>
            ))}
          </div>
        </section>
      ) : null}

      {/* Secondary action */}
      <div className="flex items-center justify-center pt-2">
        <Button
          variant="outline"
          onClick={onStartFresh}
          className="border-gray-700 text-gray-300 hover:text-white"
          data-testid="overwhelm-fresh-btn"
        >
          <RotateCcw className="w-4 h-4 mr-2" />
          Start over with a different situation
        </Button>
      </div>
    </div>
  );
};

export default GatedOverwhelmDisplay;
